/**
 * Permission Guard Components
 * Provides declarative permission-based rendering and routing protection
 */

import React, { ReactNode } from 'react';
import { UserPermissions, UserRole, SectionPermissions } from '../../lib/types';
import { usePermissions } from '../../contexts/PermissionContext';

/**
 * Base permission guard props
 */
interface BasePermissionGuardProps {
  children: ReactNode;
  fallback?: ReactNode;
  showFallback?: boolean;
}

/**
 * Section-based permission guard
 */
interface SectionGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
  operation?: keyof SectionPermissions;
}

export function SectionGuard({
  children,
  section,
  operation = 'view',
  fallback = null,
  showFallback = true
}: SectionGuardProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    // While permissions load, optimistically render children to avoid placeholder skeletons.
    return <>{children}</>;
  }

  if (!hasPermission(section, operation)) {
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

/**
 * Role-based guard
 */
interface RoleGuardProps extends BasePermissionGuardProps {
  roles: UserRole | UserRole[];
  requireAll?: boolean;
}

export function RoleGuard({
  children,
  roles,
  requireAll = false,
  fallback = null,
  showFallback = true
}: RoleGuardProps) {
  const { hasRole, hasAnyRole, isLoading } = usePermissions();

  if (isLoading) {
    // While permissions load, optimistically render children to avoid placeholder skeletons.
    return <>{children}</>;
  }

  const rolesArray = Array.isArray(roles) ? roles : [roles];
  let hasAccess = false;

  if (requireAll) {
    // User must have ALL specified roles (though typically a user only has one role)
    hasAccess = rolesArray.every(role => hasRole(role));
  } else {
    // User must have ANY of the specified roles
    hasAccess = hasAnyRole(rolesArray);
  }

  if (!hasAccess) {
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

/**
 * Admin-only guard
 */
export function AdminGuard({
  children,
  fallback = null,
  showFallback = true
}: BasePermissionGuardProps) {
  return (
    <RoleGuard
      roles={UserRole.Admin}
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </RoleGuard>
  );
}

/**
 * View permission guard - shorthand for common view checks
 */
interface ViewGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
}

export function ViewGuard({ children, section, fallback = null, showFallback = true }: ViewGuardProps) {
  return (
    <SectionGuard
      section={section}
      operation="view"
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </SectionGuard>
  );
}

/**
 * Edit permission guard - shorthand for common edit checks
 */
interface EditGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
}

export function EditGuard({ children, section, fallback = null, showFallback = true }: EditGuardProps) {
  return (
    <SectionGuard
      section={section}
      operation="edit"
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </SectionGuard>
  );
}

/**
 * Execute permission guard - for actions like running sync jobs
 */
interface ExecuteGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
}

export function ExecuteGuard({ children, section, fallback = null, showFallback = true }: ExecuteGuardProps) {
  return (
    <SectionGuard
      section={section}
      operation="execute"
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </SectionGuard>
  );
}

/**
 * Export permission guard - for export functionality
 */
interface ExportGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
}

export function ExportGuard({ children, section, fallback = null, showFallback = true }: ExportGuardProps) {
  return (
    <SectionGuard
      section={section}
      operation="export"
      fallback={fallback}
      showFallback={showFallback}
    >
      {children}
    </SectionGuard>
  );
}

/**
 * Combined permission guard - requires multiple permissions
 */
interface MultiPermissionGuardProps extends BasePermissionGuardProps {
  permissions: Array<{
    section: keyof UserPermissions;
    operation: keyof SectionPermissions;
  }>;
  requireAll?: boolean;
}

export function MultiPermissionGuard({
  children,
  permissions,
  requireAll = true,
  fallback = null,
  showFallback = true
}: MultiPermissionGuardProps) {
  const { hasPermission, isLoading } = usePermissions();

  if (isLoading) {
    return <div className="animate-pulse bg-gray-200 h-4 rounded"></div>;
  }

  let hasAccess = false;

  if (requireAll) {
    // User must have ALL specified permissions
    hasAccess = permissions.every(perm => hasPermission(perm.section, perm.operation));
  } else {
    // User must have ANY of the specified permissions
    hasAccess = permissions.some(perm => hasPermission(perm.section, perm.operation));
  }

  if (!hasAccess) {
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

/**
 * Button with permission-based disabled state
 */
interface PermissionButtonProps {
  section: keyof UserPermissions;
  operation: keyof SectionPermissions;
  onClick: () => void;
  children: ReactNode;
  className?: string;
  disabled?: boolean;
  disabledMessage?: string;
}

export function PermissionButton({
  section,
  operation,
  onClick,
  children,
  className = '',
  disabled = false,
  disabledMessage = 'You do not have permission to perform this action'
}: PermissionButtonProps) {
  const { hasPermission } = usePermissions();

  const canPerformAction = hasPermission(section, operation);
  const isDisabled = disabled || !canPerformAction;

  const buttonClass = `${className} ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`;

  const handleClick = () => {
    if (!isDisabled) {
      onClick();
    }
  };

  return (
    <button
      onClick={handleClick}
      disabled={isDisabled}
      className={buttonClass}
      title={!canPerformAction ? disabledMessage : undefined}
    >
      {children}
    </button>
  );
}

/**
 * Navigation guard for protecting routes
 */
interface NavGuardProps extends BasePermissionGuardProps {
  section: keyof UserPermissions;
  operation?: keyof SectionPermissions;
  roles?: UserRole | UserRole[];
  requireAllRoles?: boolean;
}

export function NavGuard({
  children,
  section,
  operation = 'view',
  roles,
  requireAllRoles = false,
  fallback = <div className="text-center p-8 text-gray-500">Access Denied</div>,
  showFallback = true
}: NavGuardProps) {
  const { hasPermission, hasRole, hasAnyRole, isLoading } = usePermissions();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
        <span className="ml-2 text-gray-600">Loading permissions...</span>
      </div>
    );
  }

  // Check section permission
  const hasRequiredPermission = hasPermission(section, operation);

  // Check role permission if specified
  let hasRequiredRole = true;
  if (roles) {
    const rolesArray = Array.isArray(roles) ? roles : [roles];
    if (requireAllRoles) {
      hasRequiredRole = rolesArray.every(role => hasRole(role));
    } else {
      hasRequiredRole = hasAnyRole(rolesArray);
    }
  }

  if (!hasRequiredPermission || !hasRequiredRole) {
    return showFallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}

/**
 * Conditional render based on loading state and permissions
 */
interface ConditionalRenderProps {
  condition: boolean;
  loading?: boolean;
  loadingComponent?: ReactNode;
  children: ReactNode;
  fallback?: ReactNode;
}

export function ConditionalRender({
  condition,
  loading = false,
  loadingComponent = <div className="animate-pulse bg-gray-200 h-4 rounded"></div>,
  children,
  fallback = null
}: ConditionalRenderProps) {
  if (loading) {
    return <>{loadingComponent}</>;
  }

  return condition ? <>{children}</> : <>{fallback}</>;
}

/**
 * Utility component for debugging permissions
 */
export function PermissionDebug() {
  const { user, permissions, isLoading } = usePermissions();

  if (process.env.NODE_ENV !== 'development') {
    return null;
  }

  if (isLoading) {
    return <div>Loading permissions...</div>;
  }

  return (
    <details className="mt-4 p-4 bg-gray-100 rounded">
      <summary className="cursor-pointer text-sm font-medium">Permission Debug Info</summary>
      <pre className="mt-2 text-xs overflow-auto">
        {JSON.stringify({ user, permissions }, null, 2)}
      </pre>
    </details>
  );
}
